import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import { paymentsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import '../App.css';
import './PaymentGateway.css';

const PaymentGateway = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [processing, setProcessing] = useState(true);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Validating payment...');
  const [error, setError] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const validatePayment = async () => {
      let token = searchParams.get('token');

      if (!token) {
        const urlString = window.location.search;
        if (urlString && urlString.startsWith('?=')) {
          token = urlString.substring(2);
        } else if (urlString && urlString.startsWith('?')) {
          const params = urlString.substring(1).split('&');
          if (params.length > 0) {
            const firstParam = params[0];
            if (firstParam.startsWith('=')) {
              token = firstParam.substring(1);
            } else if (!firstParam.includes('=')) {
              token = firstParam;
            }
          }
        }
      }

      if (!token) {
        setStatus('error');
        setMessage('No payment token provided');
        setError('Missing payment token in URL');
        setProcessing(false);
        return;
      }

      const savedOrder = sessionStorage.getItem('vague_last_order');
      if (!savedOrder) {
        setStatus('error');
        setMessage('Order information not found');
        setError('Could not find order information. Please try again or contact support.');
        setProcessing(false);
        return;
      }

      let orderId;
      try {
        const orderData = JSON.parse(savedOrder);
        orderId = orderData.order_id || orderData.id || orderData.orderId;
      } catch (err) {
        console.error('Error parsing order data:', err);
        setStatus('error');
        setMessage('Invalid order information');
        setError('Could not parse order information. Please contact support.');
        setProcessing(false);
        return;
      }

      if (!orderId) {
        setStatus('error');
        setMessage('Order ID not found');
        setError('Could not find order ID. Please contact support.');
        setProcessing(false);
        return;
      }

      try {
        const response = await paymentsAPI.validateToken(token, orderId);

        if (response.success) {
          const savedOrder = sessionStorage.getItem('vague_last_order');
          if (savedOrder && response.data?.downloadInfo) {
            try {
              const orderData = JSON.parse(savedOrder);
              orderData.downloadToken = response.data.downloadInfo.downloadToken;
              orderData.downloadPassword = response.data.downloadInfo.password;
              orderData.downloadExpiresAt = response.data.downloadInfo.expiresAt;
              sessionStorage.setItem('vague_last_order', JSON.stringify(orderData));
            } catch (err) {
              console.error('Error updating sessionStorage:', err);
            }
          }

          clearCart();

          setStatus('success');
          setMessage('Payment validated successfully!');

          setTimeout(() => {
            navigate(`/confirmation/${orderId}`, { replace: true });
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Payment validation failed');
          setError(response.message || 'Unknown error occurred');
        }
      } catch (err) {
        console.error('Payment validation error:', err);
        setStatus('error');
        setMessage('Payment validation failed');
        setError(err.message || 'Failed to validate payment. Please contact support if the payment was completed.');
      } finally {
        setProcessing(false);
      }
    };

    validatePayment();
  }, [searchParams, navigate]);

  const menuItems = [
    { type: 'link', label: 'Home', to: '/' },
    { type: 'link', label: 'Vague', to: '/' },
    { type: 'link', label: 'Shop', to: '/shop' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
      className="payment-gateway-page"
    >
      <ResponsiveNavbar
        menuItems={menuItems}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />

      <div className="payment-gateway-container">
        <div className="payment-gateway-content">
          {processing ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="payment-processing"
            >
              <div className="processing-spinner">
                <div className="spinner"></div>
              </div>
              <h2 className="payment-status-title">{message}</h2>
              <p className="payment-status-subtitle">Please wait while we verify your payment...</p>
            </motion.div>
          ) : status === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="payment-success"
            >
              <div className="payment-icon success">✓</div>
              <h2 className="payment-status-title">Payment Successful!</h2>
              <p className="payment-status-subtitle">{message}</p>
              <p className="payment-redirect-message">Redirecting to your order confirmation...</p>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="payment-error"
            >
              <div className="payment-icon error">✕</div>
              <h2 className="payment-status-title">Payment Validation Failed</h2>
              <p className="payment-status-subtitle">{message}</p>
              {error && (
                <div className="error-details">
                  <p>{error}</p>
                </div>
              )}
              <div className="payment-error-actions">
                <button
                  onClick={() => navigate('/shop')}
                  className="payment-button primary"
                >
                  Return to Shop
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="payment-button secondary"
                >
                  Try Again
                </button>

              </div>

            </motion.div>

          )}
        </div>

      </div>

    </motion.div>

  );
};

export default PaymentGateway;

